import { useEffect, useRef, useState } from "react";
import { Music4, Play, Square, Zap, ListMusic, Clock } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { audioUrl } from "@/lib/api";

const LADDER = ["calm", "explore", "reference", "tension", "combat"];

// Two <audio> elements cross-fade between the theme's states as intensity moves along the ladder.
export default function ThemeMixer() {
  const [themes, setThemes] = useState([]);
  const [themeId, setThemeId] = useState("");
  const [intensity, setIntensity] = useState(0.5);
  const [playing, setPlaying] = useState(false);
  const a = useRef(null); const b = useRef(null); const cur = useRef(null);
  const [override, setOverride] = useState(null); // state forced by a cue
  const [clock, setClock] = useState(null); // timeline playback: seconds elapsed
  const timers = useRef([]);

  useEffect(() => { api.get("/themes").then(({ data }) => setThemes(data.filter((t) => t.status === "completed"))).catch(() => {}); }, []);
  const theme = themes.find((t) => t.id === themeId);
  const states = theme ? LADDER.filter((s) => (s === "reference" ? theme.reference?.status === "completed" : theme.variation_tracks?.[s]?.status === "completed")) : [];
  const idx = Math.min(states.length - 1, Math.floor(intensity * states.length));
  const active = override && states.includes(override) ? override : states[idx];
  const cues = [...(theme?.cues || [])].sort((x, y) => (x.at_sec ?? 1e9) - (y.at_sec ?? 1e9));
  const stingers = theme?.stingers || {};
  const fireCue = (c) => { setOverride(c.state); };
  const fireStinger = (k) => { const s = new Audio(audioUrl(stingers[k].path)); s.play().catch(() => {}); };
  const clearTimeline = () => { timers.current.forEach(clearInterval); timers.current.forEach(clearTimeout); timers.current = []; setClock(null); };
  const playTimeline = () => {
    if (!cues.length) return;
    clearTimeline(); setPlaying(true);
    const t0 = performance.now();
    cues.filter((c) => c.at_sec !== null && c.at_sec !== undefined).forEach((c) => timers.current.push(setTimeout(() => fireCue(c), c.at_sec * 1000)));
    timers.current.push(setInterval(() => setClock((performance.now() - t0) / 1000), 250));
  };
  const src = (s) => audioUrl(s === "reference" ? theme.reference.audio_files.mp3 : theme.variation_tracks[s].audio_files.mp3);

  useEffect(() => {
    if (!playing || !active || !theme) return;
    if (cur.current === active) return;
    const from = a.current.dataset.state === cur.current ? a.current : b.current;
    const to = from === a.current ? b.current : a.current;
    to.src = src(active); to.dataset.state = active; to.loop = true; to.volume = 0; to.currentTime = 0; to.play().catch(() => {});
    cur.current = active;
    const t0 = performance.now();
    const tick = () => {
      const k = Math.min(1, (performance.now() - t0) / 1500);
      to.volume = k; from.volume = 1 - k;
      if (k < 1) requestAnimationFrame(tick); else { from.pause(); }
    };
    requestAnimationFrame(tick);
  }, [active, playing, theme]);

  const stop = () => { clearTimeline(); setOverride(null); [a, b].forEach((r) => { if (!r.current) return; r.current.pause(); r.current.removeAttribute("src"); delete r.current.dataset.state; }); cur.current = null; setPlaying(false); };
  const start = () => { if (!theme || !states.length) return; setPlaying(true); };
  useEffect(() => () => { [a, b].forEach((r) => r.current?.pause()); timers.current.forEach(clearInterval); timers.current.forEach(clearTimeout); }, []);
  useEffect(() => { setOverride(null); }, [intensity]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5 space-y-4" data-testid="theme-mixer">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold flex items-center gap-2"><Music4 size={16} className="text-primary" /> Theme mixer <span className="text-xs text-muted-foreground font-normal">— slide intensity, hear calm → tension → combat cross-fade live</span></h3>
        <Select value={themeId} onValueChange={(v) => { stop(); setThemeId(v); }}><SelectTrigger className="w-56 h-9" data-testid="theme-mixer-select"><SelectValue placeholder={themes.length ? "Choose a theme…" : "No completed theme yet"} /></SelectTrigger><SelectContent>{themes.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
      </div>
      {theme && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {states.map((s, i) => <Badge key={s} variant="outline" className={`capitalize ${s === active && playing ? "border-primary text-primary" : "text-muted-foreground"}`} data-testid={`theme-mixer-state-${s}`}>{i + 1}. {s}</Badge>)}
            {theme.key && <span className="text-[11px] font-mono text-muted-foreground ml-auto">{theme.key} · {theme.bpm} BPM</span>}
          </div>
          <div className="flex items-center gap-4">
            <Button size="sm" onClick={playing ? stop : start} className={playing ? "bg-destructive/80 hover:bg-destructive text-white gap-1.5" : "bg-primary text-primary-foreground hover:bg-primary/90 gap-1.5"} data-testid="theme-mixer-toggle">{playing ? <Square size={13} /> : <Play size={13} />} {playing ? "Stop" : "Play"}</Button>
            <div className="flex-1 flex items-center gap-3"><span className="text-xs text-muted-foreground w-14">Intensity</span><Slider value={[intensity]} min={0} max={1} step={0.01} onValueChange={([v]) => setIntensity(v)} data-testid="theme-mixer-intensity" /><span className="font-mono text-xs w-10 text-right">{intensity.toFixed(2)}</span></div>
          </div>
          {(cues.length > 0 || Object.keys(stingers).length > 0) && (
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/60" data-testid="theme-mixer-cues">
              {cues.length > 0 && <><ListMusic size={13} className="text-muted-foreground" />
                {cues.map((c) => <button key={c.id || c.event} type="button" onClick={() => { setPlaying(true); fireCue(c); }} className={`px-2.5 py-1 rounded-md border text-xs ${override === c.state && playing ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`} title={`→ ${c.state}${c.at_sec != null ? ` @ ${c.at_sec}s` : ""}`} data-testid={`theme-mixer-cue-${c.event}`}>{c.event} <span className="opacity-60">→ {c.state}</span></button>)}
                <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" onClick={clock === null ? playTimeline : clearTimeline} data-testid="theme-mixer-timeline"><Clock size={12} /> {clock === null ? "Play timeline" : `${clock.toFixed(0)}s · stop`}</Button></>}
              {Object.keys(stingers).length > 0 && <><Zap size={13} className="text-muted-foreground ml-2" />
                {Object.keys(stingers).map((k) => <button key={k} type="button" onClick={() => fireStinger(k)} className="px-2.5 py-1 rounded-md border border-border text-xs capitalize text-muted-foreground hover:text-primary hover:border-primary" data-testid={`theme-mixer-stinger-${k}`}>{k}</button>)}</>}
            </div>
          )}
        </>
      )}
      <audio ref={a} preload="none" /><audio ref={b} preload="none" />
    </div>
  );
}
